<template>
  <div class="container-fluid">
    <Breadcrumbs main="Рейтинг" title="Мировой рейтинг" />
    <b-card>
      <header class="d-flex justify-content-between mt-3">
        <SearchRating class="w-25" />
        <Actualize :actualize-function="actualizeRating" />
      </header>
      <b-nav tabs class="mt-3">
        <b-nav-item
          v-for="tab in divisionTabs"
          :key="tab.slug || 'all'"
          :active="activeDivision === tab.slug"
          @click="setDivision(tab.slug)"
        >
          {{ tab.label }}
        </b-nav-item>
      </b-nav>
      <body class="d-flex mt-3">
      <b-table :items="teamsMlbbRatingList" :fields="teamsMlbbRatingFields">
        <template v-slot:cell(country)="{ item }">
          {{ item.country?.id }} {{item.country?.code && '(' + item.country.code + ')' }}
        </template>
        <template v-slot:cell(division)="{ item }">
          {{ getDivisionLabel(item) }}
        </template>
        <template v-slot:cell(action)="{ item }">
          <b-btn
            v-if="isInFirstDivision(item)"
            variant="danger"
            size="sm"
            :disabled="pendingTeamId === item.id"
            @click="unbindFirstDivision(item)"
          >
            Убрать из 1 дивизиона
          </b-btn>
          <b-btn
            v-else
            variant="success"
            size="sm"
            :disabled="pendingTeamId === item.id"
            @click="bindFirstDivision(item)"
          >
            Добавить в 1 дивизион
          </b-btn>
        </template>
      </b-table>
      </body>
      <div class="d-flex justify-content-center">
        <PaginationRating :total="paginationTotal" class="mt-3 mb-3" />
      </div>
    </b-card>
  </div>
</template>

<script>
import Vue from "vue";
import api from "@/api/api";

//components
import SearchRating from "@/pages/Rating/components/SearchRating.vue";
import PaginationRating from "@/pages/Rating/components/PaginationRating.vue";
import Actualize from "@/pages/Rating/components/Actualize.vue";

const FIRST_DIVISION_SLUG = 'mlbb-division-1';

const DIVISION_TABS = [
  {slug: null, label: 'Все'},
  {slug: 'mlbb-division-1', label: '1 дивизион'},
  {slug: 'mlbb-division-2', label: '2 дивизион'},
  {slug: 'mlbb-division-3', label: '3 дивизион'},
];

const DIVISION_LABEL_BY_SLUG = {
  'mlbb-division-1': '1 дивизион',
  'mlbb-division-2': '2 дивизион',
  'mlbb-division-3': '3 дивизион',
};

const BASE_FIELDS = [
  {key: 'id', label: 'ID'},
  {key: 'place', label: 'Место'},
  {key: 'name', label: 'Название'},
  {key: 'rating', label: 'Рейтинг'},
  {key: 'country', label: 'Код страны'},
  {key: 'division', label: 'Дивизион'},
  {key: 'action', label: 'Действия'},
];

export default {
  components: {
    SearchRating,
    PaginationRating,
    Actualize,
  },
  data() {
    return {
      teamsMlbbRatingList: [],
      teamsMlbbRatingFields: BASE_FIELDS,
      paginationTotal: 0,
      divisionTabs: DIVISION_TABS,
      pendingTeamId: null,
    };
  },
  computed: {
    activeDivision() {
      return this.$route.query.division || null;
    },
  },
  watch: {
    "$route"() {
      this.handleQueryChange();
    },
  },
  methods: {
    // Обёртка для вызова функции актуализации
    async actualizeRating() {
      try {
        await api.mlbbTeamRating.actualize();
        console.log("Актуализация завершена");
      } catch (error) {
        console.error("Ошибка актуализации:", error);
      }
    },
    setDivision(slug) {
      if (this.activeDivision === slug) return;

      const query = {...this.$route.query};
      delete query.page;
      if (slug) {
        query.division = slug;
      } else {
        delete query.division;
      }
      this.$router.push({query});
    },
    getTeamDivisionSlug(item) {
      return item?.division?.slug || item?.division_slug || null;
    },
    getDivisionLabel(item) {
      const slug = this.getTeamDivisionSlug(item);
      if (slug && DIVISION_LABEL_BY_SLUG[slug]) return DIVISION_LABEL_BY_SLUG[slug];
      return item?.division?.name?.ru || item?.division?.name || '—';
    },
    isInFirstDivision(item) {
      if (this.activeDivision === FIRST_DIVISION_SLUG) return true;
      return this.getTeamDivisionSlug(item) === FIRST_DIVISION_SLUG;
    },
    async bindFirstDivision(item) {
      this.pendingTeamId = item.id;
      try {
        await api.teams.bindMlbbFirstDivision(item.id);
        Vue.toasted.success('Команда добавлена в 1 дивизион', {position: 'top-right', duration: 3000});
        await this.handleQueryChange();
      } catch (error) {
        Vue.toasted.error(error?.response?.data?.message || 'Не удалось добавить в 1 дивизион', {position: 'top-right', duration: 3000});
      } finally {
        this.pendingTeamId = null;
      }
    },
    async unbindFirstDivision(item) {
      this.pendingTeamId = item.id;
      try {
        await api.teams.unbindMlbbFirstDivision(item.id);
        Vue.toasted.success('Команда убрана из 1 дивизиона', {position: 'top-right', duration: 3000});
        await this.handleQueryChange();
      } catch (error) {
        Vue.toasted.error(error?.response?.data?.message || 'Не удалось убрать из 1 дивизиона', {position: 'top-right', duration: 3000});
      } finally {
        this.pendingTeamId = null;
      }
    },
    async handleQueryChange() {
      const queryParams = {
        page: this.$route.query.page || '1',
      };
      if (this.$route.query.search) {
        queryParams['filter[teams.name]'] = this.$route.query.search;
      }
      if (this.$route.query.division) {
        queryParams.division = this.$route.query.division;
      }
      this.fetchData(queryParams);
    },
    async fetchData(queryParams = {}) {
      try {
        this.teamsMlbbRatingList = [];
        const res = await api.mlbbTeamRating.index(queryParams);

        this.paginationTotal = Math.ceil(res.data.pagination.total / res.data.pagination.by);
        this.teamsMlbbRatingList = res.data.data
      } catch (error) {
        console.error("Error fetching world rating:", error);
      }
    },
  },
  created() {
    this.handleQueryChange();
  }
};
</script>
